import { useEffect, useMemo, useCallback, useState } from 'react'
import regionFilter from './regionFilter'
import DropDown from '../DropDown/DropDown'
import { useFormContext } from 'react-hook-form'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../../../ErrorCodes'
import { Body, noop } from '@panwds/react-ui'
import { CHINA_REGION, FED_MOD_REGION, PLATFORM_REGION_MISMATCH_WARNING_MESSAGE } from '../../../constants'
import { REGION_FIELD_NAME } from '../GlobalConstants'

import type { AppRegion } from '@sparky/framework'
import type { IDropDownProps } from '../DropDown/DropDown'
import type { CommonStoreState } from '../../../store'

const fieldName = REGION_FIELD_NAME

export type IRegionDropDown = Partial<Omit<IDropDownProps, 'fieldName'>> & {
  support_account_id: string,
  appIdList: Array<string>,
  isLoading: boolean,
  curTsgPlatformRegion: string | null,
  state: CommonStoreState,
  disable?: boolean,
  errorCallback?: (message: string) => void
}

const defaultProps = {
  display: {
    errorMessage: 'Select Region to proceed activation',
    toolTipMessage: 'Select the Region where the cdl needs to be provisioned',
  },
  dropDownProps: {
    required: true,
    triggerValidationOnChange: true,
  },
  size: {
    height: 32,
    width: 300
  },
  passByProps: {
    dataTestId: 'region-activation-screen',
    enableSearch: false,
    label: 'Region',
    placeholder: 'Select Region',
  }
}

const RegionDropDown = (props: IRegionDropDown) => {
  const { setValue, clearErrors, setError, formState } = useFormContext()
  const { curTsgPlatformRegion, isLoading, state, support_account_id, appIdList, callbacks, disable, errorCallback = noop } = props
  const { apps, tsg, lockedInfo } = state
  const { errors } = formState
  const [warningMessage, setWarning] = useState<string | null>(null)

  const lockedRegions = useMemo(() => {
    /**
     * conditions for lockedRegions:
     * undefined -> nothing is locked from BE, every region open
     * [] -> nothing is allowed
     * ['gov'] -> only gov is allowed
     */
    return lockedInfo?.regions
  }, [lockedInfo])

  const [regionDropDown, firstRegion] = useMemo(() => {
    const _support_account_id = lockedInfo?.csp_id ? lockedInfo?.csp_id : support_account_id
    const isFedModRegionLock = lockedRegions?.some(region => region === FED_MOD_REGION)
    // generate region list first, then do filtering based on condition
    let regionList = regionFilter({
      apps,
      supportAccountId: _support_account_id,
      appIdList,
      allowFederal: isFedModRegionLock,
    })
    if (!isLoading && (!tsg || !curTsgPlatformRegion)) {
      // no curTsgPlatformRegion
      regionList = regionList.filter((each) => {
        // APPORTAL-9696, right now have to hide it from code, no generic way doing this
        if (each.id === CHINA_REGION && !lockedRegions?.some(each => each === CHINA_REGION)) {
          return false
        }
        return true
      })
    }
    else if (!isLoading) {
      // already pick platformRegion in tsg
      regionList = regionList.filter((each) => {
        if (curTsgPlatformRegion === 'global') {
          return true
        }
        else if (curTsgPlatformRegion) {
          return each.id === curTsgPlatformRegion
        }
        return false
      })
    }
    if (lockedRegions) {
      const lockedRegionSet = new Set(lockedRegions)
      return [regionList.filter(each => lockedRegionSet.has(each.id || '')), regionList.filter(each => lockedRegionSet.has(each.id || ''))[0]]
    }
    return [regionList, regionList[0]]
  }, [appIdList, apps, curTsgPlatformRegion, isLoading, lockedInfo?.csp_id, lockedRegions, support_account_id, tsg])

  const regionWarningCb = useCallback((value) => {
    const primaryAppId = state?.manifest?.primary_app_id
    if (!primaryAppId) {
      return
    }
    const primaryAppManifest = apps.get(primaryAppId)
    if (primaryAppManifest?.flags?.platform_region_mismatch_warning) {
      const platformRegion = value.id
      const appRegionList = primaryAppManifest.regions
      const aiOpsRegion = appRegionList?.find((each: AppRegion) => each.platform_regions?.includes(platformRegion))?.name
      if (aiOpsRegion !== platformRegion && appRegionList && aiOpsRegion) {
        setWarning(PLATFORM_REGION_MISMATCH_WARNING_MESSAGE
          .replace(/{app_display}/g, primaryAppManifest.display_name)
          .replace(/{region_display}/g, appRegionList.find((each: AppRegion) => each.name === aiOpsRegion)?.display || '')
          .replace(/{platform_region_display}/g, apps.get('platform')?.regions?.find((each: any) => each.name === platformRegion)?.display || '')
        )
      }
      else {
        setWarning(null)
      }
    }
  }, [apps, state?.manifest])

  const onChangeCallback = useCallback((value) => {
    regionWarningCb(value)
    if (typeof callbacks?.changeHandlerCallback === 'function') {
      callbacks?.changeHandlerCallback(value)
    }
  }, [callbacks, regionWarningCb])

  useEffect(() => {
    if (isLoading) {
      return
    }
    if (lockedInfo?.regions?.length === 1 && regionDropDown.length === 1) {
      // if specify locked Region, auto select
      setValue(fieldName, firstRegion)
      errors?.region && clearErrors(fieldName)
      regionWarningCb(firstRegion)
      return
    }
    if (curTsgPlatformRegion && regionDropDown.length) {
      setValue(fieldName, firstRegion)
      errors?.region && clearErrors(fieldName)
      regionWarningCb(firstRegion)
      return
    }
    if (curTsgPlatformRegion && !regionDropDown.length) {
      setValue(fieldName, null)
      !errors?.region && setError(fieldName, { message: ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_REGION_AVAILABLE] })
      errorCallback(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_REGION_AVAILABLE])
      setWarning(null)
      return
    }
  }, [clearErrors, curTsgPlatformRegion, isLoading, regionDropDown.length, firstRegion, setError, setValue, errors, regionWarningCb, lockedInfo?.regions?.length, errorCallback])

  useEffect(() => {
    if (support_account_id) { // change in csp reset region to address regions with restricted regions
      //setValue(fieldName, null)
    }
  }, [setValue, support_account_id])

  return <div>
    <DropDown{...{
      ...props, dropDownList: regionDropDown, isLoading, callbacks: { changeHandlerCallback: onChangeCallback },
      disabled: disable,
      fieldName,
      display: {
        ...defaultProps.display,
        ...props.display
      },
      dropDownProps: {
        ...defaultProps.dropDownProps,
        ...props.dropDownProps,
      },
      passByProps: {
        ...defaultProps.passByProps,
        ...props.passByProps,
      },
      size: {
        ...defaultProps.size,
        ...props.size
      }
    }} />
    {warningMessage && <div className="tw-flex-1"><Body size="sm" addClassName="tw-my-1 tw-text-orange-400" appearance="secondary">
      {warningMessage}
    </Body></div>}
  </div>
}

export default RegionDropDown
